import { inject, Injectable, signal } from '@angular/core';
import { LocalStorageStore } from '@localStore';
import { TimePeriodValue } from './time-period-picker.models';
import { FormControl } from '@angular/forms';
import { addHours, startOfDay, subDays, subHours, subYears, } from 'date-fns';

/**
 * Service that manages the selected time period for a time period picker.
 * It handles loading, updating, and saving the time period to localStorage.
 */
@Injectable({ providedIn: 'root' })
export class TimePeriodPickerService {
  // Key for saving the central period range in localStorage
  private centerPeriodDatesLocalStorageKey: string = 'centerPeriodDates';
  // Default time period value used if no period is selected
  protected readonly DEFAULT_PERIOD_VALUE: TimePeriodValue = 'last24h';

  // Inject LocalStorageStore using inject function (instead of constructor injection)
  private localStorage = inject(LocalStorageStore);

  // Timestamp for now
  public readonly NOW = new Date();
  // Timestamp for last day
  public readonly LAST_DAY = subDays(this.NOW, 1);
  // Timestamp for 24 hours ago
  public readonly LAST_24H = subHours(this.NOW, 24);
  // Timestamp for 1 year ago (do NOT mutate NOW)
  public readonly LAST_YEAR = subYears(this.NOW, 1);
  // Default range dates
  public readonly DEFAULT_RANGE_DATES = [this.LAST_24H, this.NOW];

  public currentPeriod = signal<TimePeriodValue>(this.DEFAULT_PERIOD_VALUE);
  public currentPeriodTime = signal<TimePeriodValue>(this.DEFAULT_PERIOD_VALUE);
  public currentDatesRange = signal<Date[]>([]);
  // Signal for the date range calculated from the central period
  public centerPeriodDates = signal<[Date, Date]>(
    this.getTimePeriodRange(this.DEFAULT_PERIOD_VALUE),
  );

  // Variable to hold the dynamic localStorageKey
  private localStorageKey!: string;
  private datesRangelocalStorageKey!: string;

  private control: FormControl<any> | null = null;

  /**
   * Initializes the service with the provided localStorage key and form control.
   *
   * @param localStorageKey - The key to be used for saving/retrieving data from localStorage.
   * @param datesRangelocalStorageKey - The key to be used for saving/retrieving data from localStorage.
   * @param control - The key to be used for saving/retrieving data from localStorage.
   * @param dateControl - The key to be used for saving/retrieving data from localStorage.
   */
  public initialize(
    localStorageKey: string,
    datesRangelocalStorageKey: string,
    control: FormControl,
    dateControl: FormControl,
  ): void {
    this.control = control;

    // Initialize local storage key
    this.localStorageKey = localStorageKey;
    this.datesRangelocalStorageKey = datesRangelocalStorageKey;

    // Initialize the form control with the value from localStorage or the default value
    this.initializePeriod(control);
    this.initializeRangeDates(dateControl);

    // Update centerPeriodDates with the value saved in localStorage
    const centerPeriod = this.getPeriod() || this.DEFAULT_PERIOD_VALUE;
    const initialCenterPeriodDates = this.getTimePeriodRange(centerPeriod);
    this.centerPeriodDates.set(initialCenterPeriodDates);
    // Save the initial range to localStorage
    this.saveCenterPeriodDatesLocalStorage(initialCenterPeriodDates);

    // Subscribe to changes in the form control's value and update the current time period
    this.initializeControlChanges(control, dateControl);
  }

  /**
   * Sets the selected time period value and range dates.
   * @param period - The time period value to store.
   */
  public setPeriod(period: TimePeriodValue) {
    this.control?.setValue(period, { emitEvent: false });
    this.currentPeriod.set(period);
    this.savePeriodLocalStorage(period);
    const rangeDates = this.getTimePeriodRange(period);
    this.saveRangeDatesLocalStorage(rangeDates);
  }

  /**
   * Initializes the form control by subscribing to value changes and updating the selected time period.
   * It also stores the selected value in localStorage for persistence across sessions.
   *
   * @param control - The form control to be subscribed to for value changes.
   * @param dateControl
   */
  private initializeControlChanges(
    control: FormControl,
    dateControl: FormControl,
  ): void {
    control.valueChanges.subscribe((period: TimePeriodValue) => {
      const selectedPeriod = period || this.DEFAULT_PERIOD_VALUE;
      this.currentPeriod.set(selectedPeriod);
      const rangeDates = this.getTimePeriodRange(selectedPeriod);
      this.savePeriodLocalStorage(selectedPeriod);
      this.currentDatesRange.set(rangeDates);
      this.saveRangeDatesLocalStorage(rangeDates);
      // Update centerPeriodDates whenever the period changes
      const newCenterPeriodDates = this.getTimePeriodRange(selectedPeriod);
      this.centerPeriodDates.set(newCenterPeriodDates);
      this.saveCenterPeriodDatesLocalStorage(newCenterPeriodDates);
    });

    dateControl.valueChanges.subscribe((rangeDates: Date[]) => {
      this.currentDatesRange.set(rangeDates);
      this.saveRangeDatesLocalStorage(rangeDates);
    });
  }

  /**
   * Saves the central date range to localStorage.
   * @param rangeDates - The date range to save.
   */
  private saveCenterPeriodDatesLocalStorage(rangeDates: [Date, Date]) {
    if (this.centerPeriodDatesLocalStorageKey) {
      this.localStorage.setItem<[Date, Date]>(
        this.centerPeriodDatesLocalStorageKey,
        rangeDates,
      );
    }
  }

  /**
   * Initializes the form control with a value from localStorage, if available.
   * If no value is found, the control will remain uninitialized.
   *
   * @param control - The form control to initialize with the stored time period.
   */
  private initializePeriod(control: FormControl) {
    const localStorageOption = this.getPeriod() || this.DEFAULT_PERIOD_VALUE;
    if (localStorageOption) {
      this.currentPeriod.set(localStorageOption);
      control.setValue(localStorageOption);
    }
  }

  /**
   * Initializes the form control with a value from localStorage, if available.
   * If no value is found, the control will remain uninitialized.
   *
   * @param control - The form control to initialize with the stored time period.
   */
  private initializeRangeDates(control: FormControl) {
    let localStorageOption =
      this.getRangeDates() == this.currentDatesRange()
        ? this.getRangeDates()
        : this.currentDatesRange();
    if (!localStorageOption) {
      this.saveRangeDatesLocalStorage(this.DEFAULT_RANGE_DATES);
      localStorageOption = this.getRangeDates();
    }

    const safeDates: Date[] = localStorageOption ?? this.DEFAULT_RANGE_DATES;
    this.currentDatesRange.set(safeDates);
    control.setValue(safeDates);
    this.saveRangeDatesLocalStorage(safeDates);
  }

  /**
   * Saves the selected time period value to localStorage.
   *
   * @param period - The time period value to store.
   */
  private savePeriodLocalStorage(period: TimePeriodValue) {
    if (this.localStorageKey) {
      this.localStorage.setItem<TimePeriodValue>(this.localStorageKey, period);
    }
  }

  /**
   * Retrieves the stored range dates period value from localStorage.
   * If no value is found, it returns undefined.
   *
   * @returns The range dates time period value, or undefined if no value is stored.
   */
  private getRangeDates() {
    if (this.datesRangelocalStorageKey) {
      return this.localStorage.getItem<Date[]>(this.datesRangelocalStorageKey);
    }
    return [this.LAST_24H, this.NOW];
  }

  /**
   * Retrieves the stored time period value from localStorage.
   * If no value is found, it returns undefined.
   *
   * @returns The saved time period value, or undefined if no value is stored.
   */
  private getPeriod() {
    if (this.localStorageKey) {
      return this.localStorage.getItem<TimePeriodValue>(this.localStorageKey);
    }
    return undefined;
  }

  /**
   * Saves the selected range dates period value to localStorage.
   *
   * @param rangeDates - The range dates value to store.
   */
  public saveRangeDatesLocalStorage(rangeDates: Date[]) {
    if (this.datesRangelocalStorageKey) {
      this.localStorage.setItem<Date[]>(
        this.datesRangelocalStorageKey,
        rangeDates,
      );
    }
  }

  /**
   * Calculates the start and end time for a given time period option.
   *
   * This function determines the time range based on the provided `optionValue`.
   * For predefined periods like "last24h", "lastWeek", etc., it computes the
   * corresponding start and end dates. If the option requires explicit input
   * (e.g., "customDate"), it throws an error.
   *
   * @param optionValue - The identifier of the time period option.
   *                      Expected values include:
   *                      - "last24h": Last 24 hours
   *                      - "lastWeek": Last 7 days
   *                      - "lastMonth": Last 30 days
   *                      - "lastYear": Last 365 days
   *                      - "customDate": User-defined date range
   *
   * @returns A tuple `[startTime, endTime]` where:
   *          - `startTime`: The calculated start date.
   *          - `endTime`: The current date as the end date.
   *
   * @throws An error if the optionValue is "customDate" or an invalid identifier.
   */
  getTimePeriodRange(optionValue: TimePeriodValue = 'last24h'): [Date, Date] {
    const now = new Date();
    const endTime = new Date(now);

    let startTime = new Date();

    switch (optionValue) {
      case 'last24h': {
        // Desde la hora en punto actual (redondeada hacia abajo) hasta 24h atrás
        const currentHourOnTheDot = now;
        const from24hAgo = subHours(currentHourOnTheDot, 24);
        return [from24hAgo, currentHourOnTheDot];
      }
      case 'lastDay': {
        // Desde las 01:00 del día anterior hasta las 00:00 del día actual
        const todayMidnight = startOfDay(now);
        const yesterday = subDays(todayMidnight, 1);
        const yesterdayAt1am = addHours(yesterday, 1);
        return [yesterdayAt1am, todayMidnight];
      }
      case 'lastWeek':
        startTime.setDate(now.getDate() - 7);
        break;
      case 'lastMonth':
        startTime.setMonth(now.getMonth() - 1);
        break;
      case 'lastYear':
        startTime.setFullYear(now.getFullYear() - 1);
        break;
      case 'customDate':
        const dates = this.currentDatesRange();
        let dateFrom = new Date(dates[0]);
        let dateTo = new Date(dates[1]);
        return [dateFrom, dateTo];

      default:
        throw new Error(`Invalid time period option: ${optionValue}`);
    }

    return [startTime, endTime];
  }
}
